import includes from 'lodash/includes';
import values from 'lodash/values';
import isString from 'lodash/isString';
import { LocalDate, Today, Tomorrow, isBefore, isSameDay } from 'lib/localDate';
import { SORT_BY, DEFAULTS } from 'lib/enums/search';
import { PAYMENT_METHODS } from 'lib/enums/payment';

const locationMappings = {
  adl: 'Adelaide, SA, Australia',
  adelaide: 'Adelaide, SA, Australia',
  bali: 'Bali, Indonesia',
  bne: 'Brisbane, QLD, Australia',
  brisbane: 'Brisbane, QLD, Australia',
  bkk: 'Bangkok, Thailand',
  bangkok: 'Bangkok, Thailand',
  cbr: 'Canberra, ACT, Australia',
  canberra: 'Canberra, ACT, Australia',
  dps: 'Bali, Indonesia',
  hba: 'Hobart, TAS, Australia',
  hobart: 'Hobart, TAS, Australia',
  per: 'Perth, WA, Australia',
  perth: 'Perth, WA, Australia',
  syd: 'Sydney, NSW, Australia',
  sydney: 'Sydney, NSW, Australia',
};

const mapLocation = (location) => {
  const locationLower = location.toLowerCase();
  const mappedLocation = locationMappings[locationLower];

  return mappedLocation ? mappedLocation : location;
};

const VALID_SORT_CODES = values(SORT_BY).map((sortBy) => sortBy.code);

const inRangeInclusive = (number, start, end) => number >= Math.min(start, end) && number <= Math.max(start, end);

export const sanitizeLocation = ({ location, ...query }) => ({
  location: location && isString(location) ? mapLocation(location) : DEFAULTS.location,
  ...query,
});

export const sanitizeOccupants = ({ adults, children, infants, ...query }) => {
  const nextAdults = inRangeInclusive(adults, 1, 9) ? adults : DEFAULTS.adults;
  const nextChildren = inRangeInclusive(children, 0, 9) ? children : DEFAULTS.children;
  const nextInfants = inRangeInclusive(infants, 0, 9) ? infants : DEFAULTS.infants;

  return {
    ...query,
    adults: nextAdults,
    children: nextChildren,
    infants: nextInfants,
  };
};

export const sanitizeDaysToCheckIn = ({ daysToCheckIn, numberOfNights, ...query }) => {
  daysToCheckIn = Number(daysToCheckIn);
  numberOfNights = Number(numberOfNights);

  if (!daysToCheckIn > 0 || !numberOfNights > 0) return { ...query };

  const today = Today();

  const checkIn = today.plus({ days: daysToCheckIn });
  const checkOut = checkIn.plus({ days: numberOfNights });

  return {
    ...query,
    checkIn: checkIn.toString(),
    checkOut: checkOut.toString(),
  };
};

export const sanitizeStayDates = (query) => {
  const today = Today();
  const tomorrow = Tomorrow();
  let checkIn;
  let checkOut;

  try {
    checkIn = LocalDate.fromString(query.checkIn);
  } catch (error) {
    checkIn = LocalDate.fromString(DEFAULTS.checkIn());
  }

  try {
    checkOut = LocalDate.fromString(query.checkOut);
  } catch (error) {
    checkOut = LocalDate.fromString(DEFAULTS.checkOut());
  }

  if (isBefore(checkIn, today)) {
    checkIn = LocalDate.fromString(DEFAULTS.checkIn());
  }

  if (isBefore(checkOut, tomorrow)) {
    checkOut = LocalDate.fromString(DEFAULTS.checkOut());
  }

  if (isBefore(checkOut, checkIn) || isSameDay(checkOut, checkIn)) {
    checkOut = checkIn.plus({ days: 1 });
  }

  return {
    ...query,
    checkIn: checkIn.toString(),
    checkOut: checkOut.toString(),
  };
};

export const sanitizeSortOptions = (query) => {
  let { sortBy } = query;

  if (!includes(VALID_SORT_CODES, query.sortBy)) {
    sortBy = DEFAULTS.sortBy;
  }

  return { ...query, sortBy };
};

export const sanitizeSortsForBackwardsCompatibility = (query) => {
  let { sortBy } = query;

  if (query.sortBy === 'price') {
    sortBy = `price_${query.sortDir}`;
  }

  if (query.sortBy === 'deals') {
    sortBy = 'promotion';
  }

  return { ...query, sortBy, sortDir: undefined };
};

export const sanitizeFiltersForBackwardsCompatibility = (query) => {
  let { minStarRating, maxPrice, propertyTypes } = query;

  if (query.minRating) {
    minStarRating = query.minRating;
  }

  if (query.maxRate) {
    maxPrice = query.maxRate;
  }

  return { ...query, minStarRating, maxPrice, propertyTypes, minRating: undefined, maxRate: undefined, propertyCategories: undefined };
};

export const sanitizePayWith = (query) => {
  let { payWith } = query;

  if (query.payWith && !values(PAYMENT_METHODS).includes(query.payWith)) {
    payWith = PAYMENT_METHODS.CASH;
  }
  return { ...query, payWith };
};

export const sanitizePriceFilters = (query) => {
  let { maxPrice } = query;

  if (query.minPrice && query.maxPrice && query.minPrice >= query.maxPrice) {
    maxPrice = undefined;
  }

  return { ...query, maxPrice };
};

export const sanitizeClassicRewards = (query) => {
  let { classicRewards } = query;

  if (query.payWith === 'cash' && query.classicRewards) {
    classicRewards = undefined;
  }

  return { ...query, classicRewards };
};
